// src/ml/extractGarmentMask.js
import { estimateHumanLandmarks } from "./estimateHumanLandmarks";
import { GARMENT_COVERAGE } from "./garmentCoverage";


// cache pose results per imageUrl (pose is expensive)
const POSE_CACHE = new Map();

// Deduplicate in-flight requests (prevents 2–3 runs from rerenders)
const INFLIGHT_CACHE = new Map();
const inflightKey = ({ url, category, variant, modelCategory }) =>
  `${url}::${String(category)}::${String(variant || "")}::${String(modelCategory || "")}`;


// call backend once
async function segmentOnce({ imageUrl, category, variant, name, modelCategory }) {
  const res = await fetch("http://localhost:8001/segment", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
  imageUrl,
  category,
  variant: variant ?? null,
  name: name ?? null,
}),

  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Segmentation failed (${res.status}): ${err}`);
  }

  return res.json();
}

// compute bbox from alpha in dataUrl
async function computeAlphaBBoxFromDataUrl(dataUrl) {
  if (
    !dataUrl ||
    typeof dataUrl !== "string" ||
    !dataUrl.startsWith("data:image/")
  )
    return null;

  const img = new Image();
  img.crossOrigin = "anonymous";

  await new Promise((resolve) => {
    img.onload = resolve;
    img.onerror = resolve;
    img.src = dataUrl;
  });

  const w = img.width,
    h = img.height;
  if (!w || !h) return null;

  const canvas = document.createElement("canvas");

  const SCALE = 0.35; // 0.25–0.5
  const sw = Math.max(1, Math.round(w * SCALE));
  const sh = Math.max(1, Math.round(h * SCALE));

  canvas.width = sw;
  canvas.height = sh;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;

  ctx.drawImage(img, 0, 0, sw, sh);
  const d = ctx.getImageData(0, 0, sw, sh).data;

  let minX = sw,
    minY = sh,
    maxX = -1,
    maxY = -1;
  let on = 0;

  for (let y = 0; y < sh; y++) {
    for (let x = 0; x < sw; x++) {
      const a = d[(y * sw + x) * 4 + 3];
      if (a > 20) {
        on++;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < 0 || on < 50) return null;

  return {
    w,
    h,
    on,
    minX: Math.round(minX / SCALE),
    maxX: Math.round(maxX / SCALE),
    minY: Math.round(minY / SCALE),
    maxY: Math.round(maxY / SCALE),
  };
}

async function hasSleeveWidthPotential(dataUrl) {
  const bbox = await computeAlphaBBoxFromDataUrl(dataUrl);
  if (!bbox) return false;

  const { w, minX, maxX } = bbox;
  const widthFrac = (maxX - minX + 1) / Math.max(1, w);

  // torso-only masks are narrow; sleeves widen silhouette
  return widthFrac > 0.62;
}

// Score candidate mask using pose-normalized coverage + width contracts
async function scoreMaskByCoverage({ dataUrl, category, pose }) {
  const bbox = await computeAlphaBBoxFromDataUrl(dataUrl);
  if (!bbox) return -1e9;

  const { w, h, minX, minY, maxX, maxY, on } = bbox;

  // sanity: area fraction of image
  const areaFrac = on / (w * h);

  // pose normalization
  const bodyTopPx = pose?.bodyTopPx ?? 0;
  const bodyBottomPx = pose?.bodyBottomPx ?? h;
  const denom = Math.max(1, bodyBottomPx - bodyTopPx);

  const bboxTopNorm = (minY - bodyTopPx) / denom;
  const bboxBottomNorm = (maxY - bodyTopPx) / denom;
  const coverageNorm = bboxBottomNorm - bboxTopNorm;

  const widthFrac = (maxX - minX + 1) / Math.max(1, w);

  const cat = String(category || "top").toLowerCase();
  const contract = GARMENT_COVERAGE[cat] ?? GARMENT_COVERAGE.top;

  // contract targets
  const minCov = contract.minCoverage;
  const maxCov = contract.maxCoverage;
  const widthMin = contract.widthMin;
  const widthMax = contract.widthMax;

  let score = 0;

  // Prefer masks that fit within coverage window
  if (coverageNorm < minCov) score -= (minCov - coverageNorm) * 120;
  if (coverageNorm > maxCov) score -= (coverageNorm - maxCov) * 120;
  if (coverageNorm >= minCov && coverageNorm <= maxCov) score += 40;

  // Prefer width within window (loose, because photos vary)
  if (widthFrac < widthMin) score -= (widthMin - widthFrac) * 80;
  if (widthFrac > widthMax) score -= (widthFrac - widthMax) * 80;
  if (widthFrac >= widthMin && widthFrac <= widthMax) score += 20;

  // Penalize obviously wrong area
  if (areaFrac < 0.04) score -= 80; // missing large parts (e.g. sleeves gone)
  if (areaFrac > 0.75) score -= 80; // probably eating background/body

  // Slight reward for more coverage (helps sleeve-dropping cases),
  // but still bounded by the contract penalties above.
  score += Math.min(25, Math.max(0, (coverageNorm - minCov) * 60));

  return score;
}

async function clipMaskToCoverageBand(maskDataUrl, category, pose) {
  if (!maskDataUrl?.startsWith("data:image/")) return maskDataUrl;

  const img = new Image();
  img.crossOrigin = "anonymous";

  await new Promise((resolve) => {
    img.onload = resolve;
    img.onerror = resolve;
    img.src = maskDataUrl;
  });

  const w = img.width,
    h = img.height;
  if (!w || !h) return maskDataUrl;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return maskDataUrl;

  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, w, h);
  const d = imageData.data;

  // pose-normalized coords: 0 at head/top, 1 at feet/bottom (same as your scoring)
  const bodyTopPx = pose?.bodyTopPx ?? 0;
  const bodyBottomPx = pose?.bodyBottomPx ?? h;
  const denom = Math.max(1, bodyBottomPx - bodyTopPx);

  const cat = String(category || "top").toLowerCase();
  const contract = GARMENT_COVERAGE[cat] ?? GARMENT_COVERAGE.top;

  // Convert your BODY anchor (0=feet..1=head) into scoring space (0=head..1=feet)
  const anchorHF = 1 - (contract.anchor ?? 0.75);

  // Build a permissive band: top near anchor, bottom = anchor + maxCoverage
  // (slack helps with cropped/tilted photos)
  const TOP_SLACK = 0.10;
  const BOTTOM_SLACK = 0.10;

  const bandTop = Math.max(0, anchorHF - TOP_SLACK);
  const bandBottom = Math.min(
    1,
    anchorHF + (contract.maxCoverage ?? 0.5) + BOTTOM_SLACK
  );

  for (let y = 0; y < h; y++) {
    const yNorm = (y - bodyTopPx) / denom; // 0..1
    const keepRow = yNorm >= bandTop && yNorm <= bandBottom;

    if (!keepRow) {
      for (let x = 0; x < w; x++) {
        d[(y * w + x) * 4 + 3] = 0; // kill alpha
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL("image/png");
}

/**
 * Normalize image URLs so backend always receives absolute URLs
 */
function normalizeImageUrl(imageUrl) {
  if (!imageUrl) return null;

  // Already absolute
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }

  // Relative → convert to absolute using frontend origin
  if (imageUrl.startsWith("/")) {
    return `${window.location.origin}${imageUrl}`;
  }

  return imageUrl;
}

/**
 * Calls segmentation backend
 */
function refineAlphaMask(dataUrl, category, variant = null, pose = null) {
  const isOuterOpen =
    String(category || "").toLowerCase() === "outer" &&
    String(variant || "").toLowerCase() === "open";

  // Tunables (start conservative)
  const settings = {
    top: { threshold: 95, erode: 0, dilate: 2, feather: 1 },
    dress: { threshold: 70, erode: 1, dilate: 1, feather: 2 },
    bottom: { threshold: 60, erode: 0, dilate: 0, feather: 1 },
    outer: { threshold: 95, erode: 2, dilate: 0, feather: 1 },
    outer_open: { threshold: 100, erode: 2, dilate: 1, feather: 2 },
  };

  const cat = String(category || "top").toLowerCase();
  const v = String(variant || "").toLowerCase();

  const key = cat === "outer" && v === "open" ? "outer_open" : cat;

  const s = settings[key] ?? settings.top;
  console.log("[refineAlphaMask] using", { cat, variant: v, key, s });

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = img.width;
      const h = img.height;

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return resolve(dataUrl);

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, w, h);
      const d = imageData.data;

      // Extract alpha into separate buffer (0..255)
      const a0 = new Uint8ClampedArray(w * h);
      for (let i = 0; i < w * h; i++) {
        a0[i] = d[i * 4 + 3];
      }

      const idx = (x, y) => y * w + x;

      const edgeMagAt = (x, y) => {
        // simple gradient on luminance using RGB, ignores alpha
        const lum = (xx, yy) => {
          const ii = idx(xx, yy) * 4;
          const r = d[ii],
            g = d[ii + 1],
            b = d[ii + 2];
          return 0.2126 * r + 0.7152 * g + 0.0722 * b;
        };

        const x0 = Math.max(0, x - 1),
          x1 = Math.min(w - 1, x + 1);
        const y0 = Math.max(0, y - 1),
          y1 = Math.min(h - 1, y + 1);

        const gx = lum(x1, y) - lum(x0, y);
        const gy = lum(x, y1) - lum(x, y0);
        return Math.abs(gx) + Math.abs(gy);
      };

      const isNearExisting = (mask255, x, y, r = 2) => {
        for (let yy = -r; yy <= r; yy++) {
          const y2 = y + yy;
          if (y2 < 0 || y2 >= h) continue;
          for (let xx = -r; xx <= r; xx++) {
            const x2 = x + xx;
            if (x2 < 0 || x2 >= w) continue;
            if (mask255[idx(x2, y2)] === 255) return true;
          }
        }
        return false;
      };

      const rescueSleevesByTexture = (mask255) => {
        const xBand = Math.max(2, Math.floor(w * 0.22));
        const y0 = Math.floor(h * 0.10);
        const y1 = Math.floor(h * 0.62);

        const EDGE_T = 14;

        for (let y = y0; y <= y1; y++) {
          for (let x = 0; x < w; x++) {
            const inArmBand = x < xBand || x >= w - xBand;
            if (!inArmBand) continue;

            const ii = idx(x, y);

            // grow-only-from-existing
            if (mask255[ii] === 255) continue;
            if (!isNearExisting(mask255, x, y, 3)) continue;

            // only add where there is real texture/edge evidence
            if (edgeMagAt(x, y) < EDGE_T) continue;

            mask255[ii] = 255;
          }
        }
        return mask255;
      };

      // Sleeve evidence (tops only)
      const hasSleeveEvidence = (mask255) => {
        const xBand = Math.max(2, Math.floor(w * 0.18));
        const y0 = Math.floor(h * 0.12);
        const y1 = Math.floor(h * 0.55);

        let edgeHits = 0;
        let midHits = 0;

        const midX0 = Math.floor(w * 0.35);
        const midX1 = Math.floor(w * 0.65);

        for (let y = y0; y <= y1; y++) {
          for (let x = 0; x < xBand; x++) {
            if (mask255[idx(x, y)] === 255) edgeHits++;
          }
          for (let x = w - xBand; x < w; x++) {
            if (mask255[idx(x, y)] === 255) edgeHits++;
          }
          for (let x = midX0; x <= midX1; x++) {
            if (mask255[idx(x, y)] === 255) midHits++;
          }
        }

        return midHits > 200 && edgeHits > 120;
      };

      const rescueSleeveEdges = (mask255, a0Alpha, thresh) => {
        const soft = Math.max(0, thresh - 35);

        const xBand = Math.max(2, Math.floor(w * 0.20));
        const y0 = Math.floor(h * 0.10);
        const y1 = Math.floor(h * 0.60);

        for (let y = y0; y <= y1; y++) {
          for (let x = 0; x < w; x++) {
            const inArmBand = x < xBand || x >= w - xBand;
            if (!inArmBand) continue;

            const ii = idx(x, y);

            if (mask255[ii] === 255) continue;
            if (a0Alpha[ii] < soft) continue;
            if (!isNearExisting(mask255, x, y, 2)) continue;

            mask255[ii] = 255;
          }
        }
        return mask255;
      };

      // Threshold -> binary mask (kills soft background haze)
      let a = new Uint8ClampedArray(w * h);
      for (let i = 0; i < w * h; i++) {
        a[i] = a0[i] >= s.threshold ? 255 : 0;
      }

      // Sleeve rescue (tops only) — compute sleeveLike ONCE
      if (key === "top") {
        const sleeveLike = hasSleeveEvidence(a);

        if (sleeveLike) {
          a = rescueSleeveEdges(a, a0, s.threshold);
          console.log("[refineAlphaMask] top sleeve rescue applied");

          a = rescueSleevesByTexture(a);
          console.log("[refineAlphaMask] top texture sleeve rescue applied");
        } else {
          console.log(
            "[refineAlphaMask] top sleeve rescue skipped (likely sleeveless)"
          );
          console.log(
            "[refineAlphaMask] top texture sleeve rescue skipped (likely sleeveless)"
          );
        }
      }

      const keepLargestComponent = (src) => {
        const visited = new Uint8Array(w * h);
        const out = new Uint8ClampedArray(w * h);

        let bestPixels = null;
        let bestArea = 0;

        const stack = [];

        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const ii0 = idx(x, y);
            if (visited[ii0]) continue;
            if (src[ii0] !== 255) continue;

            visited[ii0] = 1;
            stack.length = 0;
            stack.push([x, y]);

            let area = 0;
            const pixels = [];

            while (stack.length) {
              const [cx, cy] = stack.pop();
              const ii = idx(cx, cy);
              pixels.push(ii);
              area++;

              if (cx > 0) {
                const i2 = idx(cx - 1, cy);
                if (!visited[i2] && src[i2] === 255) {
                  visited[i2] = 1;
                  stack.push([cx - 1, cy]);
                }
              }
              if (cx < w - 1) {
                const i2 = idx(cx + 1, cy);
                if (!visited[i2] && src[i2] === 255) {
                  visited[i2] = 1;
                  stack.push([cx + 1, cy]);
                }
              }
              if (cy > 0) {
                const i2 = idx(cx, cy - 1);
                if (!visited[i2] && src[i2] === 255) {
                  visited[i2] = 1;
                  stack.push([cx, cy - 1]);
                }
              }
              if (cy < h - 1) {
                const i2 = idx(cx, cy + 1);
                if (!visited[i2] && src[i2] === 255) {
                  visited[i2] = 1;
                  stack.push([cx, cy + 1]);
                }
              }
            }

            if (area > bestArea) {
              bestArea = area;
              bestPixels = pixels;
            }
          }
        }

        if (bestPixels) {
          for (const ii of bestPixels) out[ii] = 255;
          return out;
        }

        return src;
      };

      const erodeOnce = (src) => {
        const out = new Uint8ClampedArray(w * h);
        for (let y = 1; y < h - 1; y++) {
          for (let x = 1; x < w - 1; x++) {
            let keep = 255;
            for (let yy = -1; yy <= 1; yy++) {
              for (let xx = -1; xx <= 1; xx++) {
                if (src[idx(x + xx, y + yy)] === 0) {
                  keep = 0;
                  yy = 2;
                  break;
                }
              }
            }
            out[idx(x, y)] = keep;
          }
        }
        return out;
      };

      const dilateOnce = (src) => {
        const out = new Uint8ClampedArray(w * h);
        for (let y = 1; y < h - 1; y++) {
          for (let x = 1; x < w - 1; x++) {
            let fill = 0;
            for (let yy = -1; yy <= 1; yy++) {
              for (let xx = -1; xx <= 1; xx++) {
                if (src[idx(x + xx, y + yy)] === 255) {
                  fill = 255;
                  yy = 2;
                  break;
                }
              }
            }
            out[idx(x, y)] = fill;
          }
        }
        return out;
      };

      // Opening = erode then dilate
      for (let i = 0; i < s.erode; i++) a = erodeOnce(a);

      // outer open coats: keep only the coat (removes inner shirt/skin blobs)
      if (isOuterOpen) a = keepLargestComponent(a);

      for (let i = 0; i < s.dilate; i++) a = dilateOnce(a);

      // Feather edges a tiny bit to avoid jaggies (simple box blur on alpha)
      if (s.feather > 0) {
        const r = s.feather;
        const out = new Uint8ClampedArray(w * h);

        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            let sum = 0;
            let count = 0;
            for (let yy = -r; yy <= r; yy++) {
              const y2 = y + yy;
              if (y2 < 0 || y2 >= h) continue;
              for (let xx = -r; xx <= r; xx++) {
                const x2 = x + xx;
                if (x2 < 0 || x2 >= w) continue;
                sum += a[idx(x2, y2)];
                count++;
              }
            }
            out[idx(x, y)] = Math.round(sum / count);
          }
        }
        a = out;
      }

      // Write refined alpha back into RGBA, keep RGB as-is
      for (let i = 0; i < w * h; i++) {
        d[i * 4 + 3] = a[i];
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };

    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// Combine original RGB image with mask alpha (mask is data:image/png; original can be URL)
// + skin sampling on ORIGINAL RGB to avoid painting sleeves onto arms/skin
function applyMaskToImage(originalUrl, maskDataUrl, pose = null) {
  // hard guard
  if (
    !originalUrl ||
    !maskDataUrl ||
    typeof maskDataUrl !== "string" ||
    !maskDataUrl.startsWith("data:image/")
  ) {
    return Promise.resolve(maskDataUrl || null);
  }

  return new Promise((resolve) => {
    const imgRGB = new Image();
    const imgMask = new Image();
    imgRGB.crossOrigin = "anonymous";
    imgMask.crossOrigin = "anonymous";

    let ready = 0;
    const done = () => {
      ready++;
      if (ready < 2) return;

      const w = imgRGB.width;
      const h = imgRGB.height;

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return resolve(maskDataUrl);

      // draw RGB
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(imgRGB, 0, 0);

      const rgb = ctx.getImageData(0, 0, w, h);
      const rd = rgb.data;

      // draw mask into a temp canvas to read its alpha
      const mCanvas = document.createElement("canvas");
      mCanvas.width = w;
      mCanvas.height = h;
      const mCtx = mCanvas.getContext("2d", { willReadFrequently: true });
      if (!mCtx) return resolve(maskDataUrl);
      mCtx.drawImage(imgMask, 0, 0, w, h);

      const mask = mCtx.getImageData(0, 0, w, h).data;

      // ---- skin model from ORIGINAL RGB (not from mask/cutout) ----
      const clampi = (n, a, b) => Math.max(a, Math.min(b, n));

      const samplePatchMeanRGB = (cx, cy, r) => {
        let sr = 0,
          sg = 0,
          sb = 0,
          n = 0;
        const x0 = clampi(cx - r, 0, w - 1);
        const x1 = clampi(cx + r, 0, w - 1);
        const y0 = clampi(cy - r, 0, h - 1);
        const y1 = clampi(cy + r, 0, h - 1);

        for (let y = y0; y <= y1; y++) {
          for (let x = x0; x <= x1; x++) {
            const ii = (y * w + x) * 4;
            sr += rd[ii];
            sg += rd[ii + 1];
            sb += rd[ii + 2];
            n++;
          }
        }
        if (!n) return null;
        return [sr / n, sg / n, sb / n];
      };

      const rgbDist = (p, q) => {
        const dr = p[0] - q[0],
          dg = p[1] - q[1],
          db = p[2] - q[2];
        return Math.sqrt(dr * dr + dg * dg + db * db);
      };

      const buildSkinModel = (points) => {
        const samples = [];
        for (const pt of points) {
          const m = samplePatchMeanRGB(pt.x, pt.y, pt.r ?? 6);
          if (m) samples.push(m);
        }
        if (!samples.length) return null;

        const rs = samples.map((s) => s[0]).sort((a, b) => a - b);
        const gs = samples.map((s) => s[1]).sort((a, b) => a - b);
        const bs = samples.map((s) => s[2]).sort((a, b) => a - b);
        const mid = Math.floor(samples.length / 2);

        return { mean: [rs[mid], gs[mid], bs[mid]] };
      };

      const isSkinLikeRGB = (x, y, skinModel, T = 38) => {
        if (!skinModel) return false;
        const ii = (y * w + x) * 4;
        const p = [rd[ii], rd[ii + 1], rd[ii + 2]];
        return rgbDist(p, skinModel.mean) < T;
      };

      const skinPoints = [];
      const lms = pose?.poseLandmarks;
          if (Array.isArray(lms) && lms.length >= 33) {
        const pick = (i, r) => {
          const lm = lms[i];
          if (!lm) return;
          skinPoints.push({
            x: clampi(Math.round(lm.x * w), 0, w - 1),
            y: clampi(Math.round(lm.y * h), 0, h - 1),
            r,
          });
        };
        pick(0, 10); // nose-ish
        pick(15, 8); // left wrist-ish
        pick(16, 8); // right wrist-ish
      } else {
        skinPoints.push({ x: Math.round(w * 0.5), y: Math.round(h * 0.15), r: 12 });
      }

      const skinModel = buildSkinModel(skinPoints);
      console.log("[applyMaskToImage] skinModel", skinModel);

      // copy mask alpha into RGB alpha, BUT suppress skin in arm bands
      const xBand = Math.floor(w * 0.22);
      const y0 = Math.floor(h * 0.10);
      const y1 = Math.floor(h * 0.62);

      for (let i = 0; i < w * h; i++) {
        const a = mask[i * 4 + 3];
        if (a === 0) {
          rd[i * 4 + 3] = 0;
          continue;
        }

        const x = i % w;
        const y = (i / w) | 0;

        const inArmBand =
          (x < xBand || x >= w - xBand) && y >= y0 && y <= y1;

        if (inArmBand && skinModel && isSkinLikeRGB(x, y, skinModel, 38)) {
          rd[i * 4 + 3] = 0;
        } else {
          rd[i * 4 + 3] = a;
        }
      }

      ctx.putImageData(rgb, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };

    imgRGB.onload = done;
    imgMask.onload = done;
    imgRGB.onerror = () => resolve(maskDataUrl);
    imgMask.onerror = () => resolve(maskDataUrl);

    imgRGB.src = originalUrl;
    imgMask.src = maskDataUrl;
  });
}
// Option B: physically crop the cutout PNG to the tight non-transparent alpha bbox.
// This makes planar projection crop-invariant (full-body vs close-up).
async function cropDataUrlToAlphaBBox(dataUrl, padFrac = 0.06) {
  if (!dataUrl?.startsWith?.("data:image/")) return dataUrl;

  const img = new Image();
  img.crossOrigin = "anonymous";

  await new Promise((resolve) => {
    img.onload = resolve;
    img.onerror = resolve;
    img.src = dataUrl;
  });

  const w = img.width, h = img.height;
  if (!w || !h) return dataUrl;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return dataUrl;

  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0);

  const d = ctx.getImageData(0, 0, w, h).data;

  let minX = w, minY = h, maxX = -1, maxY = -1;
  let on = 0;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = d[(y * w + x) * 4 + 3];
      if (a > 20) {
        on++;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  // If basically empty, do nothing
  if (maxX < 0 || on < 50) return dataUrl;

  const bw = maxX - minX + 1;
  const bh = maxY - minY + 1;

  // pad in pixels, as a fraction of bbox size (keeps feathered edges safe)
  const pad = Math.round(Math.max(2, Math.max(bw, bh) * padFrac));

  const x0 = Math.max(0, minX - pad);
  const y0 = Math.max(0, minY - pad);
  const x1 = Math.min(w - 1, maxX + pad);
  const y1 = Math.min(h - 1, maxY + pad);

  const cw = Math.max(1, x1 - x0 + 1);
  const ch = Math.max(1, y1 - y0 + 1);

  const out = document.createElement("canvas");
  out.width = cw;
  out.height = ch;

  const octx = out.getContext("2d");
  if (!octx) return dataUrl;

  // Copy just the garment region
  octx.drawImage(canvas, x0, y0, cw, ch, 0, 0, cw, ch);

  return out.toDataURL("image/png");
}

function subtractMaskAlpha(outerDataUrl, bottomDataUrl, strength = 1.0) {
  // returns outerDataUrl with alpha reduced where bottomDataUrl is opaque
  if (!outerDataUrl || !bottomDataUrl) return Promise.resolve(outerDataUrl);
  if (
    typeof outerDataUrl !== "string" ||
    typeof bottomDataUrl !== "string"
  )
    return Promise.resolve(outerDataUrl);
  if (
    !outerDataUrl.startsWith("data:image/") ||
    !bottomDataUrl.startsWith("data:image/")
  )
    return Promise.resolve(outerDataUrl);

  return new Promise((resolve) => {
    const imgOuter = new Image();
    const imgBottom = new Image();
    imgOuter.crossOrigin = "anonymous";
    imgBottom.crossOrigin = "anonymous";

    let ready = 0;
    const done = () => {
      ready++;
      if (ready < 2) return;

      const w = imgOuter.width;
      const h = imgOuter.height;
      if (!w || !h) return resolve(outerDataUrl);

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return resolve(outerDataUrl);

      // read outer RGBA
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(imgOuter, 0, 0);
      const outImg = ctx.getImageData(0, 0, w, h);
      const od = outImg.data;

      // read bottom alpha
      const mCanvas = document.createElement("canvas");
      mCanvas.width = w;
      mCanvas.height = h;
      const mCtx = mCanvas.getContext("2d", { willReadFrequently: true });
      if (!mCtx) return resolve(outerDataUrl);

      mCtx.drawImage(imgBottom, 0, 0, w, h);
      const bd = mCtx.getImageData(0, 0, w, h).data;

      // subtract alpha
      for (let i = 0; i < w * h; i++) {
        const bi = bd[i * 4 + 3] / 255; // 0..1
        if (bi <= 0) continue;
        const oi = i * 4 + 3;
        const oA = od[oi] / 255;
        const newA = Math.max(0, oA - bi * strength);
        od[oi] = Math.round(newA * 255);
      }

      ctx.putImageData(outImg, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };

    imgOuter.onload = done;
    imgBottom.onload = done;
    imgOuter.onerror = () => resolve(outerDataUrl);
    imgBottom.onerror = () => resolve(outerDataUrl);

    imgOuter.src = outerDataUrl;
    imgBottom.src = bottomDataUrl;
  });
}

export async function extractGarmentMask({ imageUrl, category, variant, name, modelCategory }) {

  console.log("[extractGarmentMask]", { category, variant });
  const normalizedUrl = normalizeImageUrl(imageUrl);

  
  if (!normalizedUrl) {
    throw new Error("No image URL provided to extractGarmentMask");
  }
    const cat = String(category || "top").toLowerCase();

  const key = inflightKey({
  url: normalizedUrl,
  category: cat,
  variant,
  modelCategory: null,
});

    const existing = INFLIGHT_CACHE.get(key);
  if (existing) return existing;

  const p = (async () => {
    try {
      // ------------------------------
      // Pose once (cached) for post-process
      // ------------------------------
      let pose = POSE_CACHE.get(normalizedUrl);
      if (!pose) {
        try {
          pose = await estimateHumanLandmarks({ imageUrl: normalizedUrl });
        } catch (e) {
          console.warn("[extractGarmentMask] pose failed, using fallback", e);
          pose = { bodyTopPx: 0, bodyBottomPx: null };
        }
        POSE_CACHE.set(normalizedUrl, pose);
      }

      console.log("[extractGarmentMask] sending -> /segment", {
        imageUrl: normalizedUrl,
        category: cat,
        variant: variant ?? null,
        name: name ?? null,
      });

      const out = await segmentOnce({
        imageUrl: normalizedUrl,
        category: cat,
        variant,
        name,
        modelCategory: null,
      });

      out.maskDataUrl = out.dataUrl; // preserve original meaning

      // normalize missing output to avoid downstream crashes
      if (!out || typeof out !== "object") return { dataUrl: null };

      if (
        !out.dataUrl ||
        typeof out.dataUrl !== "string" ||
        !out.dataUrl.startsWith("data:image/")
      ) {
        return { ...out, dataUrl: null };
      }

      // Post-process mask on the client
      try {
        const refinedMask = await refineAlphaMask(out.dataUrl, category, variant, pose);

if (
  refinedMask &&
  typeof refinedMask === "string" &&
  refinedMask.startsWith("data:image/")
) {
const cutout = await applyMaskToImage(normalizedUrl, refinedMask, pose);

if (cutout?.startsWith("data:image/")) {
  // Option B: crop to alpha bbox so planar projection isn't crop-dependent
  const cropped = await cropDataUrlToAlphaBBox(cutout, 0.06); // 0.04–0.10
  out.cutoutDataUrl = cropped;
}

        } else {
          console.warn(
            "[extractGarmentMask] refineAlphaMask returned invalid data, keeping original mask"
          );
        }
      } catch (e) {
        console.warn(
          "[extractGarmentMask] post-process failed, keeping original mask",
          e
        );
      }
      // Backwards compatibility: if callers expect dataUrl to be the cutout:
out.dataUrl = out.cutoutDataUrl ?? out.dataUrl;

      return out;
    } catch (err) {
      console.error("extractGarmentMask failed:", err);
      throw err;
    } finally {
      INFLIGHT_CACHE.delete(key);
    }
  })();

  INFLIGHT_CACHE.set(key, p);
  return p;

}

